import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Pencil,
  ImageIcon,
  Film,
  Code2,
  BarChart2,
  Zap,
  Sprout,
  Rocket,
  Brain,
  Settings2,
  Gift,
  Tag,
  CreditCard,
  Diamond,
  FileText,
  MessageSquare,
  Video,
  RefreshCw,
  Lightbulb,
  Calendar,
  Compass,
} from 'lucide-react';

interface QuizAnswer {
  question1?: string;
  question2?: string;
  question3?: string;
  question4?: string;
  question5?: string;
}

interface ToolRecommendation {
  name: string;
  slug: string;
  category: string;
  description: string;
}

export default function ToolFinderQuiz() {
  const [currentQuestion, setCurrentQuestion] = useState(1);
  const [answers, setAnswers] = useState<QuizAnswer>({});
  const [showResults, setShowResults] = useState(false);
  const [showEmailCapture, setShowEmailCapture] = useState(false);
  const [email, setEmail] = useState('');
  const [emailSubmitted, setEmailSubmitted] = useState(false);

  const handleAnswer = (questionKey: keyof QuizAnswer, value: string) => {
    const newAnswers = { ...answers, [questionKey]: value };
    setAnswers(newAnswers);

    if (currentQuestion < 5) {
      setTimeout(() => {
        setCurrentQuestion(currentQuestion + 1);
      }, 300);
    } else {
      // After last question show optional email capture first
      setTimeout(() => {
        setShowEmailCapture(true);
      }, 300);
    }
  };

  const getRecommendations = (): ToolRecommendation[] => {
    const { question1, question2, question3, question4, question5 } = answers;

    // Writing tools
    if (question1 === 'writing') {
      if (question2 === 'beginner' && question3 === 'free') {
        return [
          { name: 'Rytr', slug: 'rytr', category: 'AI Writing', description: 'Free AI writing assistant for short-form content and blog posts' },
          { name: 'Copy.ai', slug: 'copy-ai', category: 'AI Writing', description: 'Generate marketing copy and content with AI' },
          { name: 'Grammarly', slug: 'grammarly', category: 'AI Writing', description: 'AI-powered writing assistant and grammar checker' }
        ];
      }
      return [
        { name: 'Jasper', slug: 'jasper', category: 'AI Writing', description: 'Professional AI writing assistant for marketers and content creators' },
        { name: 'Writesonic', slug: 'writesonic', category: 'AI Writing', description: 'AI content generator for articles, ads, and landing pages' },
        { name: 'Copy.ai', slug: 'copy-ai', category: 'AI Writing', description: 'Generate marketing copy and content with AI' }
      ];
    }

    // Image tools
    if (question1 === 'images') {
      if (question3 === 'free') {
        return [
          { name: 'DALL·E 3', slug: 'dall-e-3', category: 'AI Image', description: 'OpenAI\'s advanced AI image generator with natural language prompts' },
          { name: 'Canva AI', slug: 'canva-ai-image-generator', category: 'AI Design', description: 'Design platform with AI image generation and editing tools' },
          { name: 'Leonardo.ai', slug: 'leonardo-ai', category: 'AI Image', description: 'AI art generator with consistent character creation' }
        ];
      }
      return [
        { name: 'Midjourney', slug: 'midjourney', category: 'AI Image', description: 'Leading AI art generator for stunning, high-quality images' },
        { name: 'Adobe Firefly', slug: 'adobe-firefly', category: 'AI Image', description: 'Adobe\'s AI image generator integrated with Creative Cloud' },
        { name: 'Leonardo.ai', slug: 'leonardo-ai', category: 'AI Image', description: 'AI art generator with consistent character creation' }
      ];
    }

    // Video tools
    if (question1 === 'videos') {
      return [
        { name: 'Runway', slug: 'runway', category: 'AI Video', description: 'Advanced AI video generation and editing platform' },
        { name: 'HeyGen', slug: 'heygen', category: 'AI Video', description: 'Create AI avatar videos with text-to-speech in minutes' },
        { name: 'Invideo AI', slug: 'invideo-ai', category: 'AI Video', description: 'Turn text into professional videos with AI' }
      ];
    }

    // Code tools
    if (question1 === 'code') {
      if (question2 === 'beginner') {
        return [
          { name: 'GitHub Copilot', slug: 'github-copilot', category: 'AI Coding', description: 'AI pair programmer that suggests code as you type' },
          { name: 'Replit AI', slug: 'replit-ai', category: 'AI Coding', description: 'AI-powered code completion in browser-based IDE' },
          { name: 'Bolt.new', slug: 'bolt-new', category: 'AI Coding', description: 'Build and deploy full-stack apps with AI prompts' }
        ];
      }
      return [
        { name: 'Cursor', slug: 'cursor', category: 'AI Coding', description: 'AI-first code editor built for productivity' },
        { name: 'Devin AI', slug: 'devin-ai', category: 'AI Coding', description: 'Autonomous AI software engineer' },
        { name: 'Windsurf', slug: 'windsurf', category: 'AI Coding', description: 'AI-powered code editor with intelligent suggestions' }
      ];
    }

    // Marketing tools
    if (question1 === 'business') {
      return [
        { name: 'HubSpot AI', slug: 'hubspot-ai', category: 'AI Marketing', description: 'AI-powered CRM and marketing automation platform' },
        { name: 'Surfer SEO', slug: 'surfer-seo', category: 'AI Marketing', description: 'AI SEO tool for content optimization and ranking' },
        { name: 'Adcreative.ai', slug: 'adcreative-ai', category: 'AI Marketing', description: 'Generate high-converting ad creatives with AI' }
      ];
    }

    // Productivity tools
    if (question1 === 'automate') {
      if (question4 === 'automations') {
        return [
          { name: 'Zapier AI', slug: 'zapier-ai', category: 'AI Productivity', description: 'Automate workflows between 5000+ apps with AI' },
          { name: 'Notion AI', slug: 'notion-ai', category: 'AI Productivity', description: 'AI-powered workspace for notes, docs, and projects' },
          { name: 'Make', slug: 'make', category: 'AI Productivity', description: 'Visual automation platform with AI capabilities' }
        ];
      }
      return [
        { name: 'Notion AI', slug: 'notion-ai', category: 'AI Productivity', description: 'AI-powered workspace for notes, docs, and projects' },
        { name: 'Reclaim AI', slug: 'reclaim-ai', category: 'AI Productivity', description: 'AI calendar assistant that optimizes your schedule' },
        { name: 'Fireflies.ai', slug: 'fireflies-ai', category: 'AI Productivity', description: 'AI meeting assistant that records and transcribes' }
      ];
    }

    // Quick results / exploring
    if (question5 === 'now') {
      return [
        { name: 'ChatGPT', slug: 'chatgpt', category: 'AI Assistant', description: 'OpenAI\'s conversational AI for writing, coding, and research' },
        { name: 'Claude', slug: 'claude', category: 'AI Assistant', description: 'Anthropic\'s AI assistant for analysis and creative work' },
        { name: 'Perplexity AI', slug: 'perplexity-ai', category: 'AI Search', description: 'AI-powered search engine with cited sources' }
      ];
    }

    // Default recommendations
    return [
      { name: 'ChatGPT', slug: 'chatgpt', category: 'AI Assistant', description: 'OpenAI\'s conversational AI for writing, coding, and research' },
      { name: 'Notion AI', slug: 'notion-ai', category: 'AI Productivity', description: 'AI-powered workspace for notes, docs, and projects' },
      { name: 'Canva AI', slug: 'canva-ai-image-generator', category: 'AI Design', description: 'Design platform with AI image generation and editing tools' }
    ];
  };

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;

    try {
      const formData = new URLSearchParams();
      formData.append('email', email);
      formData.append('source', 'quiz');
      formData.append('submitted_at', new Date().toISOString());

      await fetch('https://readdy.ai/api/form/d6v4fod1l1ubpb129bs0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formData.toString(),
      });

      setEmailSubmitted(true);
      setEmail('');
    } catch (error) {
      console.error('Error submitting email:', error);
    }

    // Show results after submission
    setShowResults(true);
    setShowEmailCapture(false);
  };

  const handleSkipEmail = () => {
    setShowEmailCapture(false);
    setShowResults(true);
  };

  const resetQuiz = () => {
    setCurrentQuestion(1);
    setAnswers({});
    setShowResults(false);
    setShowEmailCapture(false);
    setEmail('');
    setEmailSubmitted(false);
  };

  const questions = [
    {
      id: 1,
      question: "What's your main goal?",
      options: [
        { icon: <Pencil size={24} strokeWidth={1.5} color="white" />, label: 'Create written content', value: 'writing' },
        { icon: <ImageIcon size={24} strokeWidth={1.5} color="white" />, label: 'Generate images', value: 'images' },
        { icon: <Film size={24} strokeWidth={1.5} color="white" />, label: 'Make videos', value: 'videos' },
        { icon: <Code2 size={24} strokeWidth={1.5} color="white" />, label: 'Write code', value: 'code' },
        { icon: <BarChart2 size={24} strokeWidth={1.5} color="white" />, label: 'Grow my business', value: 'business' },
        { icon: <Zap size={24} strokeWidth={1.5} color="white" />, label: 'Automate tasks', value: 'automate' },
      ],
    },
    {
      id: 2,
      question: "What's your experience level?",
      options: [
        { icon: <Sprout size={24} strokeWidth={1.5} color="white" />, label: 'Complete beginner', value: 'beginner' },
        { icon: <Rocket size={24} strokeWidth={1.5} color="white" />, label: 'Tried a few tools', value: 'tried' },
        { icon: <Brain size={24} strokeWidth={1.5} color="white" />, label: 'Use AI regularly', value: 'regular' },
        { icon: <Settings2 size={24} strokeWidth={1.5} color="white" />, label: 'Power user / developer', value: 'power' },
      ],
    },
    {
      id: 3,
      question: "What's your budget?",
      options: [
        { icon: <Gift size={24} strokeWidth={1.5} color="white" />, label: 'Free only', value: 'free' },
        { icon: <Tag size={24} strokeWidth={1.5} color="white" />, label: 'Under $20/month', value: 'under20' },
        { icon: <CreditCard size={24} strokeWidth={1.5} color="white" />, label: '$20–$100/month', value: '20to100' },
        { icon: <Diamond size={24} strokeWidth={1.5} color="white" />, label: 'No budget limit', value: 'unlimited' },
      ],
    },
    {
      id: 4,
      question: "What type of output do you need?",
      options: [
        { icon: <FileText size={24} strokeWidth={1.5} color="white" />, label: 'Long-form text', value: 'longform' },
        { icon: <MessageSquare size={24} strokeWidth={1.5} color="white" />, label: 'Short-form content', value: 'shortform' },
        { icon: <ImageIcon size={24} strokeWidth={1.5} color="white" />, label: 'Images', value: 'images' },
        { icon: <Video size={24} strokeWidth={1.5} color="white" />, label: 'Video', value: 'video' },
        { icon: <RefreshCw size={24} strokeWidth={1.5} color="white" />, label: 'Automations', value: 'automations' },
        { icon: <Lightbulb size={24} strokeWidth={1.5} color="white" />, label: 'Brainstorming', value: 'brainstorm' },
      ],
    },
    {
      id: 5,
      question: "How soon do you need results?",
      options: [
        { icon: <Zap size={24} strokeWidth={1.5} color="white" />, label: 'Right now', value: 'now' },
        { icon: <Calendar size={24} strokeWidth={1.5} color="white" />, label: 'Building long-term', value: 'longterm' },
        { icon: <Compass size={24} strokeWidth={1.5} color="white" />, label: 'Just exploring', value: 'exploring' },
      ],
    },
  ];

  const currentQuestionData = questions[currentQuestion - 1];
  const recommendations = showResults ? getRecommendations() : [];

  return (
    <section className="px-6 py-20 bg-[#0a0a0a]">
      <div className="max-w-[700px] mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <span className="inline-block px-3 py-1 rounded-full bg-[#0d9488]/10 border border-[#0d9488]/20 text-[#0d9488] text-xs font-semibold tracking-widest uppercase mb-5">
            Find Your Perfect Tool
          </span>
          <h2 className="text-4xl md:text-5xl font-extrabold text-white mb-5 tracking-tight leading-tight">
            AI Tool Finder Quiz
          </h2>
          <p className="text-base text-gray-500 max-w-xl mx-auto leading-relaxed">
            Answer 5 quick questions and get personalized AI tool recommendations based on your needs.
          </p>
        </div>

        {/* Quiz Card */}
        <div className="bg-gray-900 rounded-lg border border-[#2a2a2a] overflow-hidden">
          {/* Progress Bar */}
          <div className="h-2 bg-[#1a1a1a]">
            <div
              className="h-full bg-gradient-to-r from-[#0d9488] to-[#14b8a6] transition-all duration-500 ease-out"
              style={{ width: showResults || showEmailCapture ? '100%' : `${(currentQuestion / 5) * 100}%` }}
            />
          </div>

          {/* Question Content */}
          {!showResults && !showEmailCapture && (
            <div className="p-8 md:p-10">
              <div className="mb-3 text-sm text-gray-500 font-medium">
                Question {currentQuestion} of 5
              </div>
              <h3 className="text-2xl font-bold text-white mb-8">
                {currentQuestionData.question}
              </h3>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {currentQuestionData.options.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => handleAnswer(`question${currentQuestion}` as keyof QuizAnswer, option.value)}
                    className="group flex items-center gap-3 p-4 bg-[#111827] hover:bg-[#0d9488]/10 border border-[#2a2a2a] hover:border-[#0d9488]/50 rounded-lg transition-all duration-200 text-left cursor-pointer whitespace-nowrap"
                  >
                    <span className="text-2xl">{option.icon}</span>
                    <span className="text-base font-medium text-gray-300 group-hover:text-white transition-colors">
                      {option.label}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Optional Email Capture — between Q5 and results */}
          {showEmailCapture && !showResults && (
            <div className="p-8 md:p-10">
              <div className="text-center mb-8">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#0d9488]/20 mb-5">
                  <i className="ri-mail-send-line text-3xl text-[#0d9488]"></i>
                </div>
                <h3 className="text-2xl font-bold text-white mb-3">
                  Your AI Toolkit Is Ready
                </h3>
                <p className="text-sm text-gray-400 max-w-sm mx-auto leading-relaxed">
                  Enter your email to get your personalized results plus weekly AI tool recommendations — free.
                </p>
              </div>

              {!emailSubmitted ? (
                <form
                  onSubmit={handleEmailSubmit}
                  data-readdy-form
                  className="space-y-3 max-w-sm mx-auto"
                >
                  <input type="hidden" name="source" value="quiz" />
                  <input type="hidden" name="submitted_at" value={new Date().toISOString()} />
                  <input
                    type="email"
                    name="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email address"
                    className="w-full px-4 py-3 bg-[#0a0a0a] border border-[#2a2a2a] rounded-lg text-white text-sm placeholder-gray-600 focus:outline-none focus:border-[#0d9488]/50 transition-colors"
                  />
                  <p className="text-xs text-gray-600 text-center">No spam. Unsubscribe anytime.</p>
                  <button
                    type="submit"
                    className="w-full px-6 py-3 bg-[#0d9488] hover:bg-[#14b8a6] text-white text-sm font-semibold rounded-lg transition-all duration-200 whitespace-nowrap cursor-pointer"
                  >
                    Show My Results →
                  </button>
                </form>
              ) : (
                <div className="flex items-center justify-center gap-2 text-green-400 text-sm mb-4">
                  <i className="ri-check-line text-lg"></i>
                  <span>Done! Check your inbox shortly.</span>
                </div>
              )}

              <div className="text-center mt-5">
                <button
                  onClick={handleSkipEmail}
                  className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-teal-400 transition-colors cursor-pointer"
                >
                  No thanks, just show my results
                  <i className="ri-arrow-right-line text-xs"></i>
                </button>
              </div>
            </div>
          )}

          {/* Results */}
          {showResults && (
            <div className="p-8 md:p-10">
              <div className="text-center mb-8">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#0d9488]/20 mb-4">
                  <i className="ri-sparkling-line text-3xl text-[#0d9488]"></i>
                </div>
                <h3 className="text-2xl font-bold text-white mb-2">
                  Your Top AI Tool Matches
                </h3>
                <p className="text-sm text-gray-500">
                  Based on your answers, here are the best tools for you
                </p>
              </div>

              {/* Recommended Tools */}
              <div className="space-y-4 mb-6">
                {recommendations.map((tool, index) => (
                  <Link
                    key={index}
                    to={`/tool/${tool.slug}`}
                    className="group block bg-[#111827] hover:bg-[#111827]/80 border border-[#2a2a2a] hover:border-[#0d9488]/50 rounded-lg p-5 transition-all duration-200 cursor-pointer"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h4 className="text-lg font-bold text-white group-hover:text-[#0d9488] transition-colors">
                            {tool.name}
                          </h4>
                          <span className="px-2 py-0.5 rounded text-xs font-medium bg-[#0d9488]/10 text-[#0d9488] border border-[#0d9488]/20 whitespace-nowrap">
                            {tool.category}
                          </span>
                        </div>
                        <p className="text-sm text-gray-400 leading-relaxed">
                          {tool.description}
                        </p>
                      </div>
                      <i className="ri-arrow-right-line text-xl text-gray-600 group-hover:text-[#0d9488] group-hover:translate-x-1 transition-all duration-200 flex-shrink-0"></i>
                    </div>
                  </Link>
                ))}
              </div>

              {/* Retake + Explore row — visible immediately above the email form */}
              <div className="flex flex-col sm:flex-row gap-3 mb-7">
                <Link
                  to="/category/ai-productivity-tools"
                  className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-3 bg-[#0d9488] hover:bg-[#14b8a6] text-white text-sm font-semibold rounded-lg transition-all duration-200 whitespace-nowrap cursor-pointer"
                >
                  Explore All Tools
                  <i className="ri-arrow-right-line"></i>
                </Link>
                <button
                  onClick={resetQuiz}
                  className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-3 bg-[#111827] hover:bg-[#111827]/80 text-white text-sm font-semibold rounded-lg border border-[#2a2a2a] hover:border-[#0d9488]/50 transition-all duration-200 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-refresh-line"></i>
                  Retake Quiz
                </button>
              </div>

              {/* Email Signup */}
              <div className="bg-[#111827] border border-[#2a2a2a] rounded-lg p-6">
                {/* Value proposition line */}
                <p className="text-xs font-semibold text-[#0d9488] uppercase tracking-widest mb-2">
                  Stay ahead of the curve
                </p>
                <h4 className="text-base font-bold text-white mb-1.5">
                  Get personalized tool updates by email
                </h4>
                <p className="text-sm text-gray-500 mb-4">
                  Weekly picks based on your interests — new tools, pricing drops, and hidden gems.
                </p>
                {!emailSubmitted ? (
                  <form onSubmit={handleEmailSubmit} data-readdy-form className="space-y-3">
                    <input type="hidden" name="source" value="quiz" />
                    <input type="hidden" name="submitted_at" value={new Date().toISOString()} />
                    <div className="flex gap-2">
                      <input
                        type="email"
                        name="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="Enter your email address"
                        required
                        className="flex-1 px-4 py-2.5 bg-[#0a0a0a] border border-[#2a2a2a] rounded-lg text-white text-sm placeholder-gray-600 focus:outline-none focus:border-[#0d9488]/50 transition-colors"
                      />
                      <button
                        type="submit"
                        className="px-6 py-2.5 bg-[#0d9488] hover:bg-[#14b8a6] text-white text-sm font-semibold rounded-lg transition-all duration-200 whitespace-nowrap cursor-pointer"
                      >
                        Subscribe
                      </button>
                    </div>
                    <p className="text-xs text-gray-600 text-center">No spam. Unsubscribe anytime.</p>
                  </form>
                ) : (
                  <div className="flex items-center gap-2 text-green-400 text-sm">
                    <i className="ri-check-line text-lg"></i>
                    <span>Thanks! Check your email for confirmation.</span>
                  </div>
                )}
              </div>

            </div>
          )}
        </div>
      </div>
    </section>
  );
}